'use client';
import { apiSdkAtom } from '@/context/atoms/ApiSdk';
import { publicConfigAtom } from '@/context/atoms/publicConfig';
import { MessageEvent, type SocketIOEventsMap, SubscriptionPayload } from '@/context/messaging/socket-io';
import createContextHardened from '@/helper/createContextHardened';
import { useAtom } from 'jotai';
import React, { type PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { io as ioSocket, type Socket } from 'socket.io-client';
import { authMechanismAtom } from '@/context/atoms/AuthMechanism';
import useStableCallback from '@/helper/useStableCallback';
import { WebEamAuthMechanism } from '@/helper/auth/WebEamAuthMechanism';
import { authenticationAtom } from '@/context/atoms/Authentication';
import { getFullyQualifiedTopic } from '@/context/helper/utilities';
import empty from '@/helper/empty';

export interface MqttSubscription<T = unknown> {
  payload: SubscriptionPayload;
  eventHandler: (data: T) => void;
  onAcknowledge?: (subscription: SubscriptionPayload) => void;
}

interface SocketIOContextState {
  isConnected: boolean;
  isConnecting: boolean;
  /** true when ALL topic subscriptions have been acknowledged */
  isAcknowledged: boolean;
  showIndicator: boolean;
  connect: () => void;
  disconnect: () => void;
  subscribe: (key: string, subscription: MqttSubscription) => void;
  unsubscribe: (key: string) => void;
  subscriptions: Record<string, MqttSubscription>;
}

const [SocketIOContext, useSocketIO] = createContextHardened<SocketIOContextState>('SocketIO');
export { useSocketIO };

/** retry delay after a server-side handshake denial, e.g. expired token or backend outage during the auth check */
const DENIAL_RETRY_DELAY_MS = 3000;

/**
 * Interval time for subscription renewal.
 *
 * Checks if the server interface (SocketIODispatcher) still has the subscription and if not, resubscribes.
 * Will cause another ACKNOLEDGE event in case it was lost.
 */
const SUBSCRIPTION_HEARTBEAT_MS = 60000;

export type SocketIOProviderProps = PropsWithChildren;

/**
 * Provider for managing access to the socket-based MQTT event system.
 *
 * @FIXME consider renaming to better reflect the MQTT relationship
 */
export function SocketIOProvider({ children }: SocketIOProviderProps): React.JSX.Element {
  const [api] = useAtom(apiSdkAtom);
  const [publicConfig] = useAtom(publicConfigAtom);
  const [authMechanism] = useAtom(authMechanismAtom);
  const [, setAuthentication] = useAtom(authenticationAtom);
  const authToken = useMemo(() => authMechanism.authentication.authToken, [authMechanism.authentication.authToken]);

  const [isConnecting, setConnecting] = useState<boolean>(false);
  const [isConnected, setConnected] = useState<boolean>(false);
  const [showIndicator, setShowIndicator] = useState<SocketIOContextState['showIndicator']>(false);

  const socketRef = useRef<Socket<SocketIOEventsMap>>(null);
  const mqttRenewIntervalRef = useRef<number>(null);
  /** pending manual retry after a server-side handshake denial (socket.io does not auto-retry those) */
  const denialRetryTimerRef = useRef<number | undefined>(undefined);
  /** guards against overlapping token refreshes while reconnect attempts stack up */
  const refreshInFlightRef = useRef<boolean>(false);

  /**
   * The subscriptions we want to establish with the MQTT dispatcher.
   *
   * These subscriptions represent the INTENT to be subscribed, NOT that we ARE subscribed.
   * Our subscription on the dispatcher side may be dropped at any moment, i.E. when communication fails,
   * and we might not realize that fact. Therefore local subscriptions can diverge from the dispatcher.
   *
   * These issues are handled primarily via a heartbeat interval that re-supplies the dispatcher with the current state.
   *
   * The key used to register a subscription here does not have to align with any part of the mqtt topic.
   * This additional key allows us some more control when we want to assigning multiple handlers to the same topic.
   */
  const [subscriptions, setSubscriptions] = useState<Record<string, MqttSubscription>>({});

  /**
   * Using an object here to keep both the resolved topic string, as well as the payload, which is needed to unsubscribe.
   */
  const [acknowledgedTopics, setAcknowledgedTopics] = useState<Record<string, MqttSubscription['payload']>>({});
  const subscriptionsByTopic = useMemo(
    () =>
      Object.entries(subscriptions).reduce(
        (carry: Record<string, { key: string; sub: MqttSubscription }[]>, [key, sub]) => {
          const fqTopic = getFullyQualifiedTopic(sub.payload);
          const topics = Object.keys(carry);
          if (topics.includes(fqTopic)) {
            carry[fqTopic].push({ key, sub });
          } else {
            carry[fqTopic] = [{ key, sub }];
          }
          return carry;
        },
        {},
      ),
    [subscriptions],
  );

  const isAcknowledged = useMemo(() => {
    const topics = Object.keys(subscriptionsByTopic);
    const ackTopicStrings = Object.keys(acknowledgedTopics);

    for (const t of topics) {
      if (!ackTopicStrings.includes(t)) {
        return false;
      }
    }

    return true;
  }, [subscriptionsByTopic, acknowledgedTopics]);

  /** Enables the indicator and calls connect() on the socket. */
  const connect = useCallback((): void => {
    if (isConnecting) {
      return;
    }

    setShowIndicator(true);
    setConnecting(true);

    console.log('Initiating socket.io connection');
    socketRef.current?.connect();
  }, [isConnecting]);

  /** Disables the indicator and calls disconnect() on the socket */
  const disconnect = useCallback(() => {
    if (isConnecting) {
      return;
    }

    console.log('Called socket.io disconnet');
    socketRef.current?.disconnect();

    setAcknowledgedTopics({});
    setShowIndicator(false);
  }, [isConnecting]);

  const handleBrowserEvent = useStableCallback(
    (eventType: 'visibility' | 'online') => {
      if (eventType === 'visibility' && document.visibilityState !== 'visible') {
        return;
      }

      console.log('Browser Event: ' + eventType);
      if (!isConnected) {
        console.log('Reconnecting via provider (handles token refresh)...');
        connect();
      }
    },
    [isConnected, connect],
  );

  /**
   * Removes -local- subscription with given key.
   *
   * Note that this does not necessarily remove the MQTT subscription. Other keys
   * might still listen on the same topic.
   */
  const unsubscribe = useCallback((key: string): void => {
    setSubscriptions((prev) => {
      if (Object.keys(prev).includes(key)) {
        console.log('[MQTT] key removed:', key);
        const { [key]: _, ...reduced } = prev;
        return reduced;
      }

      return prev;
    });
  }, []);

  /**
   * Registers subscription locally with the given key.
   *
   * This will also attempt to emit the subscription for the server-side handler.
   * Because we can't ensure the socket exists or is connected at this stage, if the server-side subscription fails, simply
   * wait for the next renew heartbeat cycle. If a connection exists at that point, the local subscriptions should all be issued again.
   */
  const subscribe = useStableCallback(
    (key: string, subscription: MqttSubscription): void => {
      setSubscriptions((prev) => ({ ...prev, [key]: subscription }));
      console.log('[MQTT] key added:', key);

      if (isConnected && socketRef.current?.emit(MessageEvent.MQTT_SUBSCRIBE, subscription.payload)) {
        console.log('[MQTT] emit subscribe:', getFullyQualifiedTopic(subscription.payload));
      }
    },
    [isConnected],
  );

  /**
   * Issues a MQTT_SUBSCRIBE_RENEW for all subscriptions.
   * Will cause a new ACKNOWLEDGE event when the MQTT server connection or server-side subscription entry was lost.
   *
   * Any local subscriptions, that were not yet forwarded to the server-side context, will also be effectively subscribed by doing this.
   * This can make explicit SUBSCRIBE events obsolete. Note however, that it will NOT unsubscribe locally missing subscriptions from the server.
   */
  const renewMqttSubscriptions = useStableCallback(() => {
    if (socketRef.current === null || !socketRef.current.connected) {
      console.log('[MQTT] skipped renew:', 'no connection');
      return;
    }

    /** @FIXME two keys can carry the same effective subscription - currently this is not an issue, but beware! */
    Object.entries(subscriptions).forEach(([key, subscription]) => {
      console.log(`[MQTT] emit renew for "${key}" on topic:`, getFullyQualifiedTopic(subscription.payload));
      socketRef.current?.emit(MessageEvent.MQTT_SUBSCRIBE_RENEW, subscription.payload);
    });
  }, [subscriptions, isConnected]);

  const onAcknowledgeCallback = useStableCallback(
    (payload: SubscriptionPayload) => {
      const topicString = getFullyQualifiedTopic(payload);
      setAcknowledgedTopics((prev) => ({ ...prev, [topicString]: payload }));

      const topicSubs = subscriptionsByTopic[topicString] ?? [];
      topicSubs.forEach(({ key, sub }) => {
        if (sub?.onAcknowledge) {
          sub.onAcknowledge(payload);
        }
        console.log(`[MQTT] acknowledged "${key}" on topic:`, topicString);
      });
    },
    [subscriptionsByTopic],
  );

  const onCanceledCallback = useStableCallback((payload: SubscriptionPayload) => {
    const topic = getFullyQualifiedTopic(payload);
    console.log('[MQTT] canceled: ', topic);

    setAcknowledgedTopics((prev) => {
      if (Object.keys(prev).includes(topic)) {
        const { [topic]: _, ...rest } = prev;
        return rest;
      }

      return prev;
    });
  }, []);

  const onMessageCallback = useStableCallback(
    (message: unknown, sub: [string, string]) => {
      const [, topic] = sub;

      Object.entries(subscriptions)
        .filter(([, s]) => getFullyQualifiedTopic(s.payload) === topic)
        .forEach(([key, subscription]) => {
          console.log(`[MQTT] event for "${key}" on topic: ${topic}`);
          subscription.eventHandler(message);
        });
    },
    [subscriptions],
  );

  const onConnectError = useStableCallback(async (error: Error) => {
    console.log('socket.io connection error: ' + error.message);
    console.log('connect_error details:', error);
    setConnected(false);

    // The 401 can arrive as our relayed handshake error ("Handshake failed
    // (401): ...") or as an engine.io TransportError carrying the status in
    // its description.
    const isUnauthorized =
      error?.message?.includes('(401)') === true || ('description' in error && error.description === 401);

    if (isUnauthorized) {
      console.log('Authentication expired, refreshing token...');

      if (authMechanism instanceof WebEamAuthMechanism) {
        // WebEamAuth is unable to refresh the access_token at the time being.
        // So we need to trigger a logout of the full worker ui app, here.
        console.log('WebEamAuthMechanism cannot refresh. Logging out...');
        //await authMechanism.invalidateAuthentication();
        setAuthentication({
          isAuthenticated: false,
          authToken: undefined,
          user: undefined,
        });
        return;
      }

      if (refreshInFlightRef.current) {
        console.log('Token refresh already in progress, skipping duplicate refresh');
      } else {
        refreshInFlightRef.current = true;
        try {
          // Call getCurrentUser() to trigger an HTTP request that will:
          // 1. Be caught by the 401 interceptor
          // 2. Automatically refresh the token
          // 3. Update the auth token atom
          await api.getCurrentUser();

          // Write the fresh token synchronously for the next connection attempt.
          // The authToken effect below also does this, but only after a react
          // render cycle, which can lose the race against the next attempt.
          if (socketRef.current !== null) {
            socketRef.current.auth = {
              authToken: authMechanism.authentication.authToken?.token,
            };
          }
          console.log('Token refreshed, socket auth updated for next reconnect attempt');
        } catch (refreshError) {
          console.log('Token refresh failed, will retry on next connection attempt:', refreshError);
        } finally {
          refreshInFlightRef.current = false;
        }
      }
    }

    // Transport-level failures are retried by the manager itself with
    // backoff. A handshake denial from the server middleware ("Handshake
    // failed (401/500): ...") instead closes the socket for good
    // (socket.active === false) and socket.io deliberately does not retry
    // those - so we have to. Delayed, to not hammer the server while the
    // cause (expired token, backend outage during the auth check) persists.
    if (socketRef.current !== null && !socketRef.current.active && denialRetryTimerRef.current === undefined) {
      console.log(`socket.io connection denied by server, retrying in ${DENIAL_RETRY_DELAY_MS}ms`);
      denialRetryTimerRef.current = window.setTimeout(() => {
        denialRetryTimerRef.current = undefined;
        if (socketRef.current !== null && !socketRef.current.connected) {
          setConnecting(true);
          socketRef.current.connect();
        }
      }, DENIAL_RETRY_DELAY_MS);
    }
  }, []);

  useEffect(() => {
    const hostname = publicConfig.env.SOCKET_IO_HOST ?? window.location.hostname;
    const socketIoPort = publicConfig.env.SOCKET_IO_PORT ?? 3030;
    const socketIoServer = `//${hostname}:${socketIoPort}`;

    const socket = ioSocket(socketIoServer, {
      // Reconnection is handled by the socket.io manager itself: randomized
      // exponential backoff, retries failed initial attempts as well as lost
      // connections. We only add what the manager cannot do on its own:
      // refreshing an expired auth token (connect_error handler below) and
      // recovering from an explicit server-side disconnect.
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      autoConnect: false,
      // Only the access token string. Never send the full AuthToken object
      // here: it contains the long-lived refresh token, and handshakes get
      // logged server-side on relay errors.
      auth: {
        authToken: authToken?.token,
      },
    });

    socket.on(MessageEvent.MQTT_SUBSCRIPTION_ACKNOWLEDGED, onAcknowledgeCallback);
    socket.on(MessageEvent.MQTT_SUBSCRIPTION_CANCELED, onCanceledCallback);
    [MessageEvent.ORDER_UPDATE, MessageEvent.UPDATE, MessageEvent.BLUEPRINTS_UPDATE].forEach((ev) => {
      socket.on(ev, onMessageCallback);
    });

    socket.on('connect_error', onConnectError);
    socket.on('connect', () => {
      console.log('socket.io connected');
      setConnected(true);
      setConnecting(false);
    });

    socket.on('disconnect', (reason) => {
      console.log('socket.io disconnected:', reason);
      setConnected(false);
      setConnecting(false);

      // Transport-level disconnects (transport close, ping timeout, ...) are
      // reconnected by the manager itself. 'io server disconnect' is the one
      // reason it deliberately does not recover from, and 'io client
      // disconnect' means we called disconnect() intentionally.
      if (reason === 'io server disconnect' && socketRef.current !== null) {
        console.log(`Reconnecting after server-side disconnect in ${DENIAL_RETRY_DELAY_MS}ms`);
        window.setTimeout(() => {
          if (socketRef.current !== null && !socketRef.current.connected) {
            setConnecting(true);
            socketRef.current.connect();
          }
        }, DENIAL_RETRY_DELAY_MS);
      }
    });

    socketRef.current = socket;
    connect();

    // Event listeners use stableHandleBrowserEvent which always calls the latest callback
    const onVisibilityChange = (): void => handleBrowserEvent('visibility');
    const onOnline = (): void => handleBrowserEvent('online');

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('online', onOnline);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('online', onOnline);
      if (denialRetryTimerRef.current !== undefined) {
        window.clearTimeout(denialRetryTimerRef.current);
        denialRetryTimerRef.current = undefined;
      }
      if (socketRef.current !== null) {
        socketRef.current.disconnect();
        socketRef.current.removeAllListeners();
        socketRef.current = null;
      }
    };
    /** This hook has to run exactly once on-mount. Therefore no dependencies! */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (socketRef.current !== null) {
      socketRef.current.auth = {
        authToken: authToken?.token,
      };

      console.log('socket.io auth token replaced');
    }
  }, [authToken]);

  /**
   * Sets up the interval for subscription renewal.
   *
   * See callback comments for further info.
   */
  useEffect(() => {
    mqttRenewIntervalRef.current = window.setInterval(renewMqttSubscriptions, SUBSCRIPTION_HEARTBEAT_MS);

    return () => {
      if (mqttRenewIntervalRef.current !== null) {
        window.clearInterval(mqttRenewIntervalRef.current);
      }
      mqttRenewIntervalRef.current = null;
    };
  }, [renewMqttSubscriptions]);

  /** Additional renew when connection (re-)establishes */
  useEffect(() => {
    if (isConnected && !isConnecting) {
      renewMqttSubscriptions();
    }
  }, [isConnected, isConnecting, renewMqttSubscriptions]);

  /** Unsubscribe when we have acknowledgements from topics we no longer need */
  useEffect(() => {
    Object.entries(acknowledgedTopics).forEach(([topic, payload]) => {
      const topicSubs = subscriptionsByTopic[topic] ?? [];

      if (empty(topicSubs)) {
        console.log('[MQTT] emit unsubscribe:', payload);
        socketRef.current?.emit(MessageEvent.MQTT_UNSUBSCRIBE, payload);
      }
    });
  }, [subscriptionsByTopic, acknowledgedTopics]);

  return (
    <SocketIOContext.Provider
      value={{
        isConnected,
        isConnecting,
        isAcknowledged,
        showIndicator,
        connect,
        disconnect,
        subscribe,
        unsubscribe,
        subscriptions,
      }}
    >
      {children}
    </SocketIOContext.Provider>
  );
}
